import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Users, Plus, Search, MoreVertical, Shield, Mail,
  CheckCircle, XCircle, Eye, KeyRound, Building2, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { AdminLayout } from "../../components/layout/AdminLayout";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Avatar, AvatarFallback } from "../../components/ui/avatar";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import { Label } from "../../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../../components/ui/table";
import api from "../../lib/api";

const UsersPage = () => {
  const navigate = useNavigate();
  const [staff, setStaff] = useState([]);
  const [platformRoles, setPlatformRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterRole, setFilterRole] = useState("");
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [roleChangeUser, setRoleChangeUser] = useState(null);
  const [newRoleValue, setNewRoleValue] = useState("");
  const [formData, setFormData] = useState({
    email: "", password: "", full_name: "", platform_role: "super_admin",
    department: "Platform Operations", job_title: "",
  });

  useEffect(() => { fetchData(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    try {
      const [staffRes, rolesRes] = await Promise.all([
        api.get("/admin/platform-staff"),
        api.get("/rbac/roles?scope=platform"),
      ]);
      setStaff(staffRes.data?.staff || []);
      setPlatformRoles(rolesRes.data?.roles || []);
    } catch (e) {
      console.error("Failed to fetch staff:", e);
      toast.error("Failed to load platform staff");
    } finally { setLoading(false); }
  };

  const handleCreate = async () => {
    try {
      await api.post("/admin/platform-staff", formData);
      toast.success("Staff member created");
      setIsCreateOpen(false);
      setFormData({ email: "", password: "", full_name: "", platform_role: "super_admin", department: "Platform Operations", job_title: "" });
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create staff member");
    }
  };

  const handleDeactivate = async (user) => {
    if (!window.confirm(`Deactivate ${user.full_name}? They will lose platform access.`)) return;
    try {
      await api.put(`/users/${user.id}/status`, { status: "inactive" });
      toast.success(`${user.full_name} deactivated`);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const handleRoleChange = async () => {
    if (!roleChangeUser || !newRoleValue) return;
    try {
      await api.put(`/admin/platform-staff/${roleChangeUser.id}/role`, { role: newRoleValue });
      toast.success("Role updated");
      setRoleChangeUser(null);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to change role"); }
  };

  const filteredStaff = staff.filter((u) =>
    (!searchQuery || u.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) || u.email?.toLowerCase().includes(searchQuery.toLowerCase())) &&
    (!filterRole || u.role === filterRole || u.custom_role === filterRole)
  );

  const getInitials = (name) => (name || "?").split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);

  const getRoleBadge = (user) => {
    const label = user.platform_role || "Super Admin";
    if (user.role === "super_admin" && !user.custom_role) {
      return <Badge className="bg-red-50 text-red-700 border-red-200" variant="outline">Super Admin</Badge>;
    }
    return <Badge className="bg-indigo-50 text-indigo-700 border-indigo-200" variant="outline">{label}</Badge>;
  };

  return (
    <AdminLayout title="Platform Staff" variant="super_admin">
      <div className="space-y-6" data-testid="platform-staff-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold" style={{ fontFamily: "'Sora', sans-serif" }}>
              Odesie Platform Staff
            </h2>
            <p className="text-sm text-muted-foreground">Manage your internal platform team — Super Admins, Account Managers, Support</p>
          </div>
          <Button className="btn-primary" onClick={() => setIsCreateOpen(true)} data-testid="add-staff-btn">
            <Plus className="w-4 h-4 mr-2" /> Add Staff Member
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card><CardContent className="p-4 text-center">
            <p className="text-2xl font-bold" data-testid="total-staff">{staff.length}</p>
            <p className="text-sm text-muted-foreground">Total Staff</p>
          </CardContent></Card>
          <Card><CardContent className="p-4 text-center">
            <p className="text-2xl font-bold">{staff.filter(u => u.role === "super_admin" && !u.custom_role).length}</p>
            <p className="text-sm text-muted-foreground">Super Admins</p>
          </CardContent></Card>
          <Card><CardContent className="p-4 text-center">
            <p className="text-2xl font-bold">{staff.filter(u => u.custom_role).length}</p>
            <p className="text-sm text-muted-foreground">Custom Roles</p>
          </CardContent></Card>
          <Card><CardContent className="p-4 text-center">
            <p className="text-2xl font-bold">{staff.filter(u => u.is_active !== false && u.status !== "inactive").length}</p>
            <p className="text-sm text-muted-foreground">Active</p>
          </CardContent></Card>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search staff..." className="pl-10" data-testid="staff-search" />
          </div>
          <Select value={filterRole || "all"} onValueChange={(v) => setFilterRole(v === "all" ? "" : v)}>
            <SelectTrigger className="w-[200px]"><SelectValue placeholder="All Roles" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Roles</SelectItem>
              <SelectItem value="super_admin">Super Admin</SelectItem>
              {platformRoles.filter(r => !r.is_system).map(r => (
                <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Staff Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
            ) : filteredStaff.length === 0 ? (
              <div className="text-center py-12">
                <Users className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium mb-2">No staff found</h3>
                <p className="text-muted-foreground">{searchQuery ? "Try a different search" : "Add your first platform staff member"}</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Staff Member</TableHead>
                      <TableHead>Platform Role</TableHead>
                      <TableHead>Department</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Joined</TableHead>
                      <TableHead className="w-[50px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredStaff.map((u) => (
                      <TableRow key={u.id} className="cursor-pointer hover:bg-slate-50" onClick={() => navigate(`/admin/staff/${u.id}`)} data-testid={`staff-row-${u.id}`}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <Avatar className="h-9 w-9">
                              <AvatarFallback className="bg-primary/10 text-primary text-sm">{getInitials(u.full_name)}</AvatarFallback>
                            </Avatar>
                            <div>
                              <p className="font-medium">{u.full_name}</p>
                              <p className="text-sm text-muted-foreground">{u.email}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>{getRoleBadge(u)}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Building2 className="w-4 h-4 text-muted-foreground" />
                            <span className="text-sm">{u.department || "Platform Operations"}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          {u.is_active !== false && u.status !== "inactive" ? (
                            <Badge className="badge-success"><CheckCircle className="w-3 h-3 mr-1" />Active</Badge>
                          ) : (
                            <Badge className="badge-error"><XCircle className="w-3 h-3 mr-1" />Inactive</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`staff-actions-${u.id}`}>
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => navigate(`/admin/staff/${u.id}`)}>
                                <Eye className="w-4 h-4 mr-2" /> View Profile
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => { toast.info("Password reset sent (simulated)"); }}>
                                <KeyRound className="w-4 h-4 mr-2" /> Reset Password
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => { setRoleChangeUser(u); setNewRoleValue(u.custom_role || "super_admin"); }}>
                                <Shield className="w-4 h-4 mr-2" /> Change Role
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem className="text-destructive" onClick={() => handleDeactivate(u)}>
                                <XCircle className="w-4 h-4 mr-2" /> Deactivate
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Create Staff Dialog */}
      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Platform Staff</DialogTitle>
            <DialogDescription>Add a new team member to the Odesie platform</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Full Name</Label>
              <Input value={formData.full_name} onChange={(e) => setFormData({ ...formData, full_name: e.target.value })} placeholder="Jane Smith" data-testid="staff-name-input" />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="jane@odesie.com" data-testid="staff-email-input" />
            </div>
            <div className="space-y-2">
              <Label>Password</Label>
              <Input type="password" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} placeholder="Strong password" data-testid="staff-password-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Platform Role</Label>
                <Select value={formData.platform_role} onValueChange={(v) => setFormData({ ...formData, platform_role: v })}>
                  <SelectTrigger data-testid="staff-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="super_admin">Super Admin</SelectItem>
                    {platformRoles.filter(r => !r.is_system).map(r => (
                      <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Department</Label>
                <Input value={formData.department} onChange={(e) => setFormData({ ...formData, department: e.target.value })} placeholder="Platform Ops" data-testid="staff-dept-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Job Title</Label>
              <Input value={formData.job_title} onChange={(e) => setFormData({ ...formData, job_title: e.target.value })} placeholder="Account Manager" data-testid="staff-title-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateOpen(false)}>Cancel</Button>
            <Button className="btn-primary" onClick={handleCreate} data-testid="create-staff-submit">Create Staff Member</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Role Dialog */}
      <Dialog open={!!roleChangeUser} onOpenChange={() => setRoleChangeUser(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Shield className="w-5 h-5 text-[#1e3a5f]" /> Change Role</DialogTitle>
            <DialogDescription>Update platform role for {roleChangeUser?.full_name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1.5">
              <Label className="text-xs">Current Role</Label>
              <p className="text-sm font-medium">{roleChangeUser?.platform_role || "Super Admin"}</p>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">New Role</Label>
              <Select value={newRoleValue} onValueChange={setNewRoleValue}>
                <SelectTrigger className="h-9" data-testid="change-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="super_admin">Super Admin</SelectItem>
                  {platformRoles.filter(r => !r.is_system).map(r => (
                    <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setRoleChangeUser(null)}>Cancel</Button>
            <Button size="sm" onClick={handleRoleChange} data-testid="confirm-role-change">Save Role</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default UsersPage;
